/**
 * Wave 10 zod-mandate — runtime schemas for the React hook inputs.
 *
 * `queue` is a class instance — not serialisable; validated as
 * `z.unknown()` (presence-only) at the boundary. `refreshMs` is the user-
 * supplied number.
 *
 * @example
 * ```ts
 * import { UseSyncStatusInputSchema } from './Hooks.schema'
 * UseSyncStatusInputSchema.parse({ queue: q, refreshMs: 1000 })
 * ```
 */
import { z } from 'zod'

export const UseSyncStatusInputSchema = z
	.object({
		queue: z.unknown(),
		refreshMs: z.number().positive().optional(),
	})
	.strict()

export const UseConflictsInputSchema = z
	.object({
		queue: z.unknown(),
	})
	.strict()

export const UseConfirmationsInputSchema = z
	.object({
		queue: z.unknown(),
		// The handler is the load-bearing argument here (unlike the polling hooks,
		// which need only the queue), so it is checked as a real function.
		onConfirmation: z.custom<(...args: unknown[]) => unknown>(
			(v) => typeof v === 'function',
			{ message: 'onConfirmation must be a function' },
		),
	})
	.strict()

export const UseOutboxEntriesInputSchema = z
	.object({
		queue: z.unknown(),
		refreshMs: z.number().positive().optional(),
		limit: z.number().int().nonnegative().optional(),
	})
	.strict()

export type UseSyncStatusInputInput = z.infer<typeof UseSyncStatusInputSchema>
export type UseConflictsInputInput = z.infer<typeof UseConflictsInputSchema>
export type UseConfirmationsInputInput = z.infer<
	typeof UseConfirmationsInputSchema
>
export type UseOutboxEntriesInputInput = z.infer<
	typeof UseOutboxEntriesInputSchema
>
